import { Link } from "react-router-dom";
import type { Article } from "@/types/content";

type ArticleCardProps = {
  article: Article;
  variant?: "default" | "feature" | "row";
};

const formatDate = (value: string) => value.replace(/-/g, ".");

const ArticleCard = ({ article, variant = "default" }: ArticleCardProps) => {
  if (variant === "row") {
    return (
      <article className="group flex gap-4 border-b border-foreground-950/10 pb-5 last:border-b-0 last:pb-0">
        <Link to={`/article/${article.id}`} className="block h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-40">
          <img
            src={article.cover}
            alt={`${article.title} · 娱乐往事回顾配图`}
            title={`${article.title} · 考古坞旧料翻出`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-label text-xs text-foreground-500">
            <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
              {article.categoryName}
            </span>
            <span>{article.year}</span>
          </div>
          <h3 className="mt-2">
            <Link
              to={`/article/${article.id}`}
              className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-600 md:text-lg"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {article.excerpt}
          </p>
        </div>
      </article>
    );
  }

  if (variant === "feature") {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <Link to={`/article/${article.id}`} className="block h-[380px] w-full md:h-[460px]">
          <img
            src={article.cover}
            alt={`${article.title} · 热门旧料考古盘点`}
            title={`${article.title} · 考古坞`}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-transparent"></span>
        </Link>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 p-5 md:p-7">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 font-label text-xs text-background-100">
            <span className="rounded-sm bg-primary-500 px-2 py-0.5 text-background-50">
              {article.categoryName}
            </span>
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-history-line"></i>
              {article.year}
            </span>
          </div>
          <h3 className="pointer-events-auto mt-3 max-w-2xl">
            <Link
              to={`/article/${article.id}`}
              className="font-heading text-2xl font-black leading-tight text-background-50 transition-colors hover:text-accent-300 md:text-3xl"
            >
              {article.title}
            </Link>
          </h3>
          <p className="pointer-events-auto mt-2 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block">
            {article.excerpt}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50">
      <Link to={`/article/${article.id}`} className="block h-48 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title} · 旧料翻出配图`}
          title={`${article.title} · 考古坞`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 font-label text-xs text-foreground-500">
          <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-accent-900">
            {article.categoryName}
          </span>
          <span>{formatDate(article.date)}</span>
        </div>
        <h3 className="mt-3">
          <Link
            to={`/article/${article.id}`}
            className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-600"
          >
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
        <div className="mt-4 flex items-center justify-between font-label text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line"></i>
            {article.author.replace("档案室 · ", "")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {article.readTime} 分钟
          </span>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;